"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransformProject = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformObject_1 = require("./TransformObject");
const TransformSchematic_1 = require("./TransformSchematic");
const TransformMultiInstanceSchematic_1 = require("./TransformMultiInstanceSchematic");
const TransformComponent_1 = require("./TransformComponent");
const TransformProcessingResult_1 = require("./TransformProcessingResult");
const TransformProjectFont_1 = require("./TransformProjectFont");
const TransformProjectLayerSet_1 = require("./TransformProjectLayerSet");
const TransformProjectModule_1 = require("./TransformProjectModule");
const TransformImageExtraction_1 = require("./TransformImageExtraction");
const Font_1 = require("../../../Font");
const mapDrawingNetLabels_1 = require("./mapDrawingNetLabels");
class TransformProject extends TransformObject_1.TransformObject {
    constructor(args, context) {
        super(undefined, {
            ...args,
            components: new Set(),
            schematics: new Set(),
            processingResults: new Set(),
            fonts: new Set(),
            layerSetTransforms: new Set(),
            imageExtractions: new Set(),
            projectModules: new Set(),
        }, args.name, new Set([
            TransformComponent_1.TransformComponent,
            TransformProcessingResult_1.TransformProcessingResult,
            TransformProjectFont_1.TransformProjectFont,
            TransformProjectLayerSet_1.TransformProjectLayerSet,
            TransformProjectModule_1.TransformProjectModule,
            TransformSchematic_1.TransformSchematic,
            TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic,
            TransformImageExtraction_1.TransformImageExtraction,
        ]), context);
        this.netGroups = [];
    }
    get resolvedNetGroups() {
        return [...this.netGroups];
    }
    add(child) {
        this.assertCanAdd(child);
        if (child instanceof TransformProcessingResult_1.TransformProcessingResult) {
            this._sharedData.processingResults.add(child);
        }
        else if (child instanceof TransformComponent_1.TransformComponent) {
            this._sharedData.components.add(child);
        }
        else if (child instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
            this._sharedData.schematics.add(child);
        }
        else if (child instanceof TransformSchematic_1.TransformSchematic) {
            this._sharedData.schematics.add(child);
        }
        else if (child instanceof TransformProjectFont_1.TransformProjectFont) {
            this._sharedData.fonts.add(child);
        }
        else if (child instanceof TransformProjectLayerSet_1.TransformProjectLayerSet) {
            this._sharedData.layerSetTransforms.add(child);
        }
        else if (child instanceof TransformProjectModule_1.TransformProjectModule) {
            this._sharedData.projectModules.add(child);
        }
        else if (child instanceof TransformImageExtraction_1.TransformImageExtraction) {
            this._sharedData.imageExtractions.add(child);
        }
    }
    compile() {
        const schematics = Array.from(this._sharedData.schematics).map((child) => [child.compiled.designId, child.compiled]);
        const components = Array.from(this._sharedData.components).map((child) => [child.compiled.name, child.compiled]);
        const fonts = Array.from(this._sharedData.fonts).map((child) => [child.compiled.name, child.compiled]);
        const layerSets = Object.fromEntries(Array.from(this._sharedData.layerSetTransforms).map((child) => [child.compiled.id, child.compiled.layers]));
        const processingResults = {
            type: 'project-processing',
            message: '',
            innerResults: Array.from(this._sharedData.processingResults).map((child) => child.compiled),
        };
        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(processingResults);
        if (processingResults.severity !== sigma_engine_shared_1.DesignModels.BoundaryLevel.Recommendation) {
            processingResults.message = `Project processing completed with ${processingResults.severity}s`;
        }
        else {
            processingResults.message = 'Project processing completed successfully';
        }
        this.resolvePhysicalNetNames(schematics);
        const seenModuleNames = new Set();
        const projectModules = [];
        for (const pm of this._sharedData.projectModules) {
            const compiled = pm.compiled;
            if (!seenModuleNames.has(compiled.name)) {
                seenModuleNames.add(compiled.name);
                projectModules.push(compiled);
            }
        }
        const assets = Array.from(this._sharedData.imageExtractions).map((ie) => ({
            type: sigma_engine_shared_1.ProjectModelAssetType.Image,
            ref: ie.compiled.ref,
            name: ie.compiled.name,
            mimeType: ie.compiled.mimeType,
        }));
        return (0, Font_1.packModelFonts)({
            name: this._sharedData.name,
            type: this._sharedData.type,
            layerSets,
            modules: projectModules,
            fonts: {
                fonts: Object.fromEntries(fonts),
            },
            warnings: [],
            components: Object.fromEntries(components),
            designs: Object.fromEntries(schematics),
            processingResults,
            globalNets: {},
            ...(assets.length > 0 ? { assets } : {}),
        });
    }
    resolvePhysicalNetNames(schematics) {
        const allTransformSchematics = [];
        for (const child of this._sharedData.schematics) {
            if (child instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
                const multi = child;
                for (const s of multi.childSchematics) {
                    allTransformSchematics.push(s);
                }
            }
            else if (child instanceof TransformSchematic_1.TransformSchematic) {
                allTransformSchematics.push(child);
            }
        }
        if (allTransformSchematics.length === 0) {
            return;
        }
        const rootSchematics = allTransformSchematics.filter((s) => s.instance?.path?.length === 0);
        if (rootSchematics.length === 0) {
            return;
        }
        const instanceToSchematic = new Map();
        for (const s of allTransformSchematics) {
            if (s.instance) {
                instanceToSchematic.set(s.instance, s);
            }
        }
        const buildHierarchy = (schematic) => {
            const instance = schematic.instance;
            const children = [];
            if (instance?.children) {
                for (const childInstance of instance.children) {
                    const childSchematic = instanceToSchematic.get(childInstance);
                    if (childSchematic) {
                        children.push(buildHierarchy(childSchematic));
                    }
                }
            }
            return {
                pathUniqueId: instance?.pathUniqueId ?? '',
                hierarchyDepth: instance?.path?.length ?? 0,
                instance,
                combobulator: schematic.netCombobulator,
                children,
            };
        };
        const namingStrategy = rootSchematics[0].netCombobulator?.namingStrategy;
        if (!namingStrategy?.resolvePhysicalNetNames) {
            return;
        }
        const rootHierarchies = rootSchematics.map(buildHierarchy);
        const projectHierarchy = rootHierarchies.length === 1 ? rootHierarchies[0] : namingStrategy.createVirtualRootHierarchy(rootHierarchies);
        const allGroups = namingStrategy.resolvePhysicalNetNames(projectHierarchy);
        this.netGroups.length = 0;
        this.netGroups.push(...allGroups);
        this.applyPhysicalNetNames(allGroups, schematics, instanceToSchematic);
    }
    applyPhysicalNetNames(groups, schematics, instanceToSchematic) {
        const modelsByDesignId = new Map();
        for (const [designId, model] of schematics) {
            modelsByDesignId.set(designId, model);
        }
        const multiInstanceDesignIds = new Set();
        for (const schematic of instanceToSchematic.values()) {
            if (!modelsByDesignId.has(schematic.designId)) {
                const parent = schematic.parent;
                if (parent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
                    const overviewModel = modelsByDesignId.get(parent.designId);
                    if (overviewModel) {
                        modelsByDesignId.set(schematic.designId, overviewModel);
                        multiInstanceDesignIds.add(schematic.designId);
                    }
                }
            }
        }
        const confirmedNames = new Map();
        const claimedPins = new Map();
        const claimedConnections = new Map();
        const drawingNetRenames = new Map();
        const pinIdentity = (moduleRef, pinRef) => `${moduleRef} ${pinRef}`;
        for (const group of groups) {
            if (!group.physicalName) {
                continue;
            }
            for (const [instance, nets] of group.getNets()) {
                const schematic = instanceToSchematic.get(instance);
                const model = schematic ? modelsByDesignId.get(schematic.designId) : undefined;
                if (!model) {
                    continue;
                }
                const isMultiInstanceChild = schematic ? multiInstanceDesignIds.has(schematic.designId) : false;
                const instanceDesignId = schematic?.designId;
                const confirmed = confirmedNames.get(model) ?? new Set();
                confirmedNames.set(model, confirmed);
                const modelClaimedPins = claimedPins.get(model) ?? new Set();
                claimedPins.set(model, modelClaimedPins);
                const modelClaimedConnections = claimedConnections.get(model) ?? new Set();
                claimedConnections.set(model, modelClaimedConnections);
                for (const net of nets) {
                    const localName = net.name.valueOf();
                    if (localName === group.physicalName) {
                        if (!isMultiInstanceChild) {
                            confirmed.add(localName);
                        }
                        continue;
                    }
                    if (!isMultiInstanceChild && confirmed.has(localName)) {
                        continue;
                    }
                    if (isMultiInstanceChild) {
                        const instanceModuleRefs = new Set();
                        for (const mod of model.modules) {
                            if (mod.multiInstance?.instanceId === instanceDesignId) {
                                instanceModuleRefs.add(mod.ref);
                            }
                        }
                        this.claimAndRewriteModulePinNets(model, localName, group.physicalName, modelClaimedPins, pinIdentity, (mod) => instanceModuleRefs.has(mod.ref));
                        this.claimAndMoveNetConnections(model, localName, group.physicalName, modelClaimedConnections, pinIdentity, (conn) => instanceModuleRefs.has(conn.moduleRef));
                    }
                    else {
                        const modelRenames = drawingNetRenames.get(model) ?? new Map();
                        if (!modelRenames.has(localName)) {
                            modelRenames.set(localName, group.physicalName);
                        }
                        drawingNetRenames.set(model, modelRenames);
                        this.claimAndMoveNetConnections(model, localName, group.physicalName, modelClaimedConnections, pinIdentity, () => true);
                        this.claimAndRewriteModulePinNets(model, localName, group.physicalName, modelClaimedPins, pinIdentity, () => true);
                    }
                }
            }
        }
        for (const [model, renames] of drawingNetRenames) {
            this.applyDrawingNetRenames(model, renames);
        }
        const overviewModels = new Set();
        for (const designId of multiInstanceDesignIds) {
            const overview = modelsByDesignId.get(designId);
            if (overview) {
                overviewModels.add(overview);
            }
        }
        const allNetNames = new Set();
        for (const model of new Set(modelsByDesignId.values())) {
            for (const name of Object.keys(model.nets)) {
                allNetNames.add(name);
            }
        }
        for (const model of new Set(modelsByDesignId.values())) {
            if (overviewModels.has(model)) {
                continue;
            }
            for (const [layer, drawings] of Object.entries(model.drawings)) {
                model.drawings[layer] = drawings.map((drawing) => (0, mapDrawingNetLabels_1.mapDrawingNetLabels)(drawing, (net) => (allNetNames.has(net) ? undefined : null)));
            }
        }
    }
    applyDrawingNetRenames(model, renames) {
        if (renames.size === 0) {
            return;
        }
        for (const [layer, drawings] of Object.entries(model.drawings)) {
            model.drawings[layer] = drawings.map((drawing) => (0, mapDrawingNetLabels_1.mapDrawingNetLabels)(drawing, (net) => renames.get(net)));
        }
    }
    claimAndMoveNetConnections(model, localName, physicalName, claimedConnections, pinIdentity, isEligibleConnection) {
        const netData = model.nets[localName];
        if (!netData) {
            return;
        }
        const movedConns = [];
        const remainingConns = [];
        for (const conn of netData.connections) {
            const identity = pinIdentity(conn.moduleRef, conn.pinRef);
            if (isEligibleConnection(conn) && !claimedConnections.has(identity)) {
                claimedConnections.add(identity);
                movedConns.push(conn);
            }
            else {
                remainingConns.push(conn);
            }
        }
        if (movedConns.length === 0) {
            return;
        }
        netData.connections = remainingConns;
        if (model.nets[physicalName]) {
            model.nets[physicalName].connections.push(...movedConns);
        }
        else {
            model.nets[physicalName] = { name: physicalName, connections: movedConns };
        }
        if (remainingConns.length === 0) {
            delete model.nets[localName];
        }
    }
    claimAndRewriteModulePinNets(model, localName, physicalName, claimedPins, pinIdentity, isEligibleModule) {
        for (const mod of model.modules) {
            if (!isEligibleModule(mod)) {
                continue;
            }
            for (const [pinRef, pinNet] of Object.entries(mod.pinNets)) {
                if (pinNet !== localName) {
                    continue;
                }
                const identity = pinIdentity(mod.ref, pinRef);
                if (claimedPins.has(identity)) {
                    continue;
                }
                claimedPins.add(identity);
                mod.pinNets[pinRef] = physicalName;
            }
        }
    }
}
exports.TransformProject = TransformProject;
//# sourceMappingURL=TransformProject.js.map